import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Intake } from "../models/Intake";
import { IntakeService } from "../services/IntakeService";
import { useAuth } from "./AuthContext";
import { IntakeMessage } from "../models/IntakeMessage";
import { Participant } from "../models/Participant";
import { useSocket } from "./SocketContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface IntakeContextProps {
  selectedIntake: Intake | null;
  setSelectedIntakeId: (intakeId: string) => void;
  messages: IntakeMessage[];
  setMessages: React.Dispatch<React.SetStateAction<IntakeMessage[]>>;
  participants: Participant[];
  intakes: Intake[];
  loading: Loadings;
  error: Errors;
  createIntake: (
    data: Omit<
      Intake,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
    >,
  ) => Promise<string>;
  updateIntake: (
    intakeId: string,
    matterData: Partial<Omit<Intake, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteIntakeById: (intakeId: string) => Promise<void>;
}

const IntakeContext = createContext<IntakeContextProps | undefined>(undefined);

export const useIntake = (): IntakeContextProps => {
  const context = useContext(IntakeContext);
  if (!context) {
    throw new Error("useIntake must be used within an IntakeProvider");
  }
  return context;
};

export const IntakeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();

  const [selectedIntake, setSelectedIntake] = useState<Intake | null>(null);
  const [selectedIntakeId, setSelectedIntakeId] = useState<string | null>(null);

  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [messages, setMessages] = useState<IntakeMessage[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);

  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  const fetchIntakes = useCallback(async () => {
    if (!user) return;
    setLoadings((prev) => ({ ...prev, list: true }));
    try {
      const data = await IntakeService.getIntakes();
      setIntakes(data);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      console.error("Failed to fetch intakes:", err);
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  }, [user]);

  const fetchSelectedIntakeDetails = useCallback(async (id: string) => {
    setLoadings((prev) => ({ ...prev, details: true }));
    try {
      const [intake, messages, participants] = await Promise.all([
        IntakeService.getIntake(id),
        IntakeService.getMessages(id),
        IntakeService.getParticipants(id),
      ]);
      setSelectedIntake(intake);
      setMessages(messages ?? []);
      setParticipants(participants ?? []);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      console.error("Failed to fetch intake details:", err);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  }, []);

  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  useEffect(() => {
    if (selectedIntakeId) {
      fetchSelectedIntakeDetails(selectedIntakeId);
    }
  }, [selectedIntakeId, fetchSelectedIntakeDetails]);

  useSocketEventByIds("intakes:changed", ["*"], async (updatedDocument) => {
    if (selectedIntakeId && updatedDocument._id === selectedIntakeId) {
      fetchSelectedIntakeDetails(selectedIntakeId);
    }
  });

  const createIntake = useCallback(
    async (
      data: Omit<
        Intake,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
      >,
    ): Promise<string> => {
      if (!user) throw new Error("User must be authenticated");
      return await IntakeService.createIntake({
        ...data,
        status: "initiated",
        createdBy: user.uid,
      });
    },
    [user],
  );

  const updateIntake = useCallback(
    async (
      intakeId: string,
      data: Partial<Omit<Intake, "_id" | "createdAt" | "updatedAt">>,
    ) => {
      await IntakeService.updateIntake(intakeId, data);
    },
    [],
  );

  const deleteIntakeById = useCallback(async (intakeId: string) => {
    await IntakeService.deleteIntake(intakeId);
    setIntakes((prev) => prev.filter((intake) => intake._id !== intakeId));
  }, []);

  return (
    <IntakeContext.Provider
      value={{
        selectedIntake,
        setSelectedIntakeId,
        intakes,
        messages,
        setMessages,
        participants,
        loading: loadings,
        error: errors,
        createIntake,
        updateIntake,
        deleteIntakeById,
      }}
    >
      {children}
    </IntakeContext.Provider>
  );
};
